import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { OkyanusViewer } from "@/viewer-core/OkyanusViewer";
import {
  loadDefinitionGeometry,
  loadLightRevision,
  loadRevision,
} from "@/viewer-core/assetLoader";
import { FACTORY_STATUS_COLORS, statusColor } from "@/constants/workflow";

/**
 * Keep-alive store for built viewers (e.g. the Factory model view): re-entering
 * the page re-attaches the existing WebGL canvas instead of reloading from scratch.
 */
const liveViewers = new Map();

/**
 * React wrapper around the framework-agnostic viewer core.
 * Keeps all Three.js state outside React to avoid re-render churn.
 */
export function useViewer({
  containerRef,
  projectId,
  revisionId,
  panels,
  colorMode,
  compareColors,
  onSelect,
  lockedUids,
  enabled = true,
  background,
  ghostUnselected,
  cacheKey,
}) {
  const viewerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [progress, setProgress] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [loadTelemetry, setLoadTelemetry] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // Light-first loading: boxes now, detailed geometry only on request.
  const lightRef = useRef(null);
  const [detailMode, setDetailMode] = useState("light");
  const [detailProgress, setDetailProgress] = useState(null);
  const [detailAvailable, setDetailAvailable] = useState(false);

  useEffect(() => {
    if (!containerRef.current || !enabled) return undefined;
    if (cacheKey && liveViewers.has(cacheKey)) {
      // Keep-alive hit: re-attach the existing viewer, no reload from scratch.
      const viewer = liveViewers.get(cacheKey);
      viewer.reattach(containerRef.current);
      viewer.onSelectionChange = (uids) => onSelectRef.current?.(uids);
      viewerRef.current = viewer;
      setManifest(viewer._cachedManifest || null);
      // Re-entering always starts light again so no detail geometry lingers in memory.
      viewer.releaseDetail();
      lightRef.current = viewer._lightData || null;
      setDetailAvailable(!!viewer._lightData);
      setDetailMode("light");
      setReady(true);
      return () => {
        viewerRef.current = null;
      };
    }
    const viewer = new OkyanusViewer(containerRef.current, { background, ghostUnselected });
    viewerRef.current = viewer;
    viewer.onSelectionChange = (uids) => onSelectRef.current?.(uids);
    return () => {
      if (cacheKey) {
        // A new revision replaces the old live viewer so stale GPU contexts are freed.
        const pagePrefix = cacheKey.split(":").slice(0, 2).join(":");
        liveViewers.forEach((cached, key) => {
          if (key !== cacheKey && key.startsWith(pagePrefix)) {
            cached.dispose();
            liveViewers.delete(key);
          }
        });
        liveViewers.set(cacheKey, viewer);
      } else {
        viewer.dispose();
      }
      viewerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, cacheKey]);

  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    if (!revisionId || !viewerRef.current) return;
    // Keep-alive path: the cached viewer is already built — nothing to load.
    if (viewerRef.current.meshes.length) {
      setReady(true);
      return;
    }
    let cancelled = false;
    setReady(false);
    setLoadError(null);
    (async () => {
      try {
        const onManifest = (m) => {
          setManifest(m);
          if (viewerRef.current) {
            viewerRef.current.mmPerUnit = m?.mmPerUnit || 1;
            viewerRef.current._cachedManifest = m;
          }
        };
        const light = await loadLightRevision({
          projectId,
          revisionId,
          onManifest,
          onProgress: (p) => !cancelled && setProgress(p),
        });
        if (cancelled) return;
        const buildStart = performance.now();
        if (light.boxesAvailable) {
          viewerRef.current.buildLight(light.definitions, light.instances);
          lightRef.current = light;
          viewerRef.current._lightData = light;
          setDetailAvailable(true);
          setDetailMode("light");
          setLoadTelemetry({
            ...light.telemetry,
            buildMs: Math.round(performance.now() - buildStart),
            totalMs: Math.round(light.telemetry?.totalMs || 0),
          });
        } else {
          // Legacy (non-quantised) package: no box bounds in the manifest — load it all.
          const result = await loadRevision({
            projectId,
            revisionId,
            onManifest,
            onProgress: (p) => !cancelled && setProgress(p),
          });
          if (cancelled) return;
          viewerRef.current.build(result.definitions, result.instances);
          viewerRef.current.buildAcceleration();
          setDetailAvailable(false);
          setDetailMode("full");
          setLoadTelemetry({
            ...result.telemetry,
            buildMs: Math.round(performance.now() - buildStart),
            totalMs: Math.round(result.telemetry?.totalMs || 0),
          });
        }
        setReady(true);
        setProgress(null);
        // Debug/testing handle (harmless in production).
        window.__oknViewer = viewerRef.current;
      } catch (error) {
        if (cancelled) return;
        toast.error(`Model load failed: ${error.message}`);
        setLoadError(error.message || "Model load failed");
        setProgress(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, revisionId]);

  const statusMap = useMemo(() => {
    const map = new Map();
    if (colorMode === "original") return map;
    if (colorMode === "factory") {
      // Factory status colours; "Awaiting Cutting" maps to null = normal look.
      panels.forEach((panel) => {
        map.set(
          panel.panel_uid,
          FACTORY_STATUS_COLORS[panel.factory_status || "Awaiting Cutting"] ?? null
        );
      });
      return map;
    }
    panels.forEach((panel) => {
      map.set(panel.panel_uid, statusColor(colorMode, panel.statuses?.[colorMode]));
    });
    return map;
  }, [panels, colorMode]);

  useEffect(() => {
    if (!ready || !viewerRef.current) return;
    viewerRef.current.setStatusColors(statusMap);
    viewerRef.current.setColorMode(colorMode);
  }, [ready, statusMap, colorMode]);

  useEffect(() => {
    if (!ready || !viewerRef.current) return;
    viewerRef.current.setCompareColors(compareColors);
  }, [ready, compareColors]);

  useEffect(() => {
    if (!ready || !viewerRef.current) return;
    viewerRef.current.setLocked(lockedUids);
  }, [ready, lockedUids]);

  /**
   * Loads detailed geometry for the given panels (all panels when uids is empty)
   * and switches them over. On failure the light view is kept.
   */
  const loadDetail = useCallback(
    async (uids) => {
      const viewer = viewerRef.current;
      const light = lightRef.current;
      if (!viewer || !light) return;
      const target = uids?.length ? [...new Set(uids)] : null;
      const defIndices = new Set();
      if (target) {
        target.forEach((uid) => {
          const meta = viewer.panelMeta.get(uid);
          if (meta) defIndices.add(meta.defIndex);
        });
      } else {
        light.instances.forEach((inst) => defIndices.add(inst.def));
      }
      const pending = [...defIndices].filter((index) => !viewer.detailLoaded.has(index));
      setDetailProgress({ loaded: 0, total: pending.length });
      try {
        for (let n = 0; n < pending.length; n += 1) {
          const definition = light.definitions[pending[n]];
          if (definition) {
            const parts = await loadDefinitionGeometry({
              projectId,
              revisionId,
              definition,
              hash: light.hash,
            });
            viewer.addDetailDefinition(pending[n], parts);
          }
          setDetailProgress({ loaded: n + 1, total: pending.length });
          // Yield so the UI stays responsive between definitions.
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
        const shown = target || [...viewer.panelMeta.keys()];
        viewer.setDetailUids(shown);
        setDetailMode(target ? "selected" : "full");
      } catch (error) {
        toast.error(`Detailed geometry failed: ${error.message}`);
        viewer.setDetailUids([]);
        setDetailMode("light");
      } finally {
        setDetailProgress(null);
      }
    },
    [projectId, revisionId]
  );

  const backToLight = useCallback(() => {
    viewerRef.current?.releaseDetail();
    setDetailMode("light");
  }, []);

  /** Returns "Detail selected" panels to their boxes; geometry stays cached. */
  const clearDetailSelection = useCallback(() => {
    viewerRef.current?.setDetailUids([]);
    setDetailMode("light");
  }, []);

  return {
    viewer: viewerRef,
    ready,
    progress,
    manifest,
    loadTelemetry,
    loadError,
    detailMode,
    detailProgress,
    detailAvailable,
    loadDetail,
    backToLight,
    clearDetailSelection,
  };
}
